import { cn } from "@/core/utils";
import { toneTextClass } from "../utils/tone";
import type { KpiGroupBlock } from "../schemas/blocks";
import { ArrowUp, ArrowDown, Minus } from "lucide-react";

/**
 * Renderer do block `kpi_group` (ST-4 — Figma Estratégia).
 *
 * Dois formatos de card no mesmo grupo:
 *  - `identity`: card de identidade (ex.: nome do escritório), valor em destaque, sem tone;
 *  - `metric` (default): caption + valor colorido pelo `tone` (regra de negócio do backend).
 *
 * O `value` já vem formatado pelo backend (moeda/locale) — o front não formata número.
 */
function DeltaBadge({ delta }: { delta: NonNullable<KpiItem["delta"]> }) {
  const Icon =
    delta.direction === "up"
      ? ArrowUp
      : delta.direction === "down"
        ? ArrowDown
        : Minus;
  return (
    <span className="inline-flex items-center gap-0.5 text-xs text-darkgray-500">
      <Icon className="h-3 w-3" aria-hidden />
      {delta.value}
    </span>
  );
}

type KpiItem = KpiGroupBlock["items"][number];

export function KpiGroup({ block }: { block: KpiGroupBlock }) {
  if (!block.items.length) return null;
  return (
    // Container query: responde à largura do container do chat (não da tela). No chat
    // reduzido fica 1 card por linha; conforme alarga, 2 e depois 3.
    <div className="@container">
      <div className="grid grid-cols-1 gap-2 @[380px]:grid-cols-2 @[560px]:grid-cols-3">
        {block.items.map((item, index) => {
          const isIdentity = item.variant === "identity";
          return (
            <div
              key={`${item.caption}-${index}`}
              className="flex flex-col gap-1 rounded-xl border border-lightgray-300 bg-white p-3"
            >
              <span className="text-xs font-medium text-darkgray-500">
                {item.caption}
              </span>
              <span
                className={cn(
                  "font-semibold leading-tight",
                  isIdentity ? "text-base text-darkgray-900" : "text-lg",
                  !isIdentity && toneTextClass(item.tone),
                )}
              >
                {item.value}
              </span>
              {item.delta && <DeltaBadge delta={item.delta} />}
              {item.hint && (
                <span className="text-xs text-darkgray-400">{item.hint}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
